import React, { useState, useMemo } from 'react';
import {
  ClipboardList, Search, Eye, X, Check, CheckCircle, FileText,
  Edit2, User, Phone, Mail, Calendar, MapPin, Euro, Plus, Trash2,
  Shield, AlertCircle
} from 'lucide-react';
import {
  inscriptions, produits, factures, getProduit,
  calculerTotalInscription, getFactureByInscription,
  formatMontant, formatDate, calculerAge, getCoursProduits,
  getInstrumentProduits, getInstrumentsForCours
} from '../data/mockData';

const Inscriptions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [selectedInscription, setSelectedInscription] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editProduitsIds, setEditProduitsIds] = useState([]);

  const nbEnAttente = inscriptions.filter(i => i.statut === 'en_attente').length;
  const nbValidees = inscriptions.filter(i => i.statut === 'validee').length;
  const nbFacturees = inscriptions.filter(i => i.statut === 'facturee').length;

  const filtered = useMemo(() => {
    return inscriptions.filter(i => {
      const matchSearch = !searchTerm ||
        `${i.eleve.prenom} ${i.eleve.nom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.eleve.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatut = filterStatut === 'tous' || i.statut === filterStatut;
      return matchSearch && matchStatut;
    });
  }, [searchTerm, filterStatut]);

  const getStatusBadge = (statut) => {
    const map = {
      en_attente: <span className="badge badge-warning">En attente</span>,
      validee: <span className="badge badge-primary">Validée</span>,
      facturee: <span className="badge badge-success">Facturée</span>,
    };
    return map[statut] || <span className="badge">{statut}</span>;
  };

  const openDetail = (inscription) => {
    setSelectedInscription(inscription);
    setEditMode(false);
    setEditProduitsIds([...inscription.produitsIds]);
  };

  const closeModal = () => {
    setSelectedInscription(null);
    setEditMode(false);
  };

  const startEdit = () => {
    setEditProduitsIds([...selectedInscription.produitsIds]);
    setEditMode(true);
  };

  const toggleProduit = (produitId) => {
    setEditProduitsIds(prev =>
      prev.includes(produitId)
        ? prev.filter(id => id !== produitId)
        : [...prev, produitId]
    );
  };

  const editTotal = editProduitsIds.reduce((sum, pid) => {
    const p = getProduit(pid);
    return sum + (p ? p.tarif : 0);
  }, 0);

  const selectedCoursIds = editProduitsIds.filter(pid => {
    const p = getProduit(pid);
    return p && p.categorie === 'cours';
  });

  const availableInstruments = getInstrumentProduits().filter(inst => {
    if (!inst.coursAssocies || inst.coursAssocies.length === 0) return true;
    return inst.coursAssocies.some(cid => selectedCoursIds.includes(cid));
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1><ClipboardList size={28} /> Inscriptions</h1>
          <div className="page-meta">
            <span>{inscriptions.length} inscription(s)</span>
            <span style={{ color: 'var(--warning)' }}>{nbEnAttente} en attente</span>
            <span style={{ color: 'var(--primary)' }}>{nbValidees} validée(s)</span>
            <span style={{ color: 'var(--success)' }}>{nbFacturees} facturée(s)</span>
          </div>
        </div>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="stat-card stat-warning">
          <div className="stat-icon"><AlertCircle size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">En attente</p>
            <p className="stat-value">{nbEnAttente}</p>
          </div>
        </div>
        <div className="stat-card stat-primary">
          <div className="stat-icon"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Validées</p>
            <p className="stat-value">{nbValidees}</p>
          </div>
        </div>
        <div className="stat-card stat-success">
          <div className="stat-icon"><FileText size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Facturées</p>
            <p className="stat-value">{nbFacturees}</p>
          </div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-input-group">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher par nom d'élève…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-tabs">
          {[
            { id: 'tous', label: 'Toutes', count: inscriptions.length },
            { id: 'en_attente', label: 'En attente', count: nbEnAttente },
            { id: 'validee', label: 'Validées', count: nbValidees },
            { id: 'facturee', label: 'Facturées', count: nbFacturees },
          ].map(f => (
            <button
              key={f.id}
              className={`filter-tab ${filterStatut === f.id ? 'active' : ''}`}
              onClick={() => setFilterStatut(f.id)}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>
      </div>

      <div className="factures-table-container">
        <table className="factures-table">
          <thead>
            <tr>
              <th>Élève</th>
              <th>Date inscription</th>
              <th>Produits</th>
              <th>Total</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(ins => {
              const total = calculerTotalInscription(ins);
              const nbProduits = ins.produitsIds.length;
              return (
                <tr key={ins.id} onClick={() => openDetail(ins)}>
                  <td>
                    <div className="famille-cell">
                      <div className="famille-avatar-mini" style={{ background: ins.color || '#8B5CF6' }}>
                        {ins.initials || `${ins.eleve.prenom[0]}${ins.eleve.nom[0]}`}
                      </div>
                      <div>
                        <strong>{ins.eleve.prenom} {ins.eleve.nom}</strong>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                          {calculerAge(ins.eleve.dateNaissance)} ans
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{formatDate(ins.dateInscription)}</td>
                  <td>
                    <div className="cours-tags">
                      {ins.produitsIds.slice(0, 3).map(pid => {
                        const p = getProduit(pid);
                        if (!p) return null;
                        return (
                          <span key={pid} className="cours-tag" style={p.categorie === 'instrument' ? { background: '#FDF2F8', color: '#EC4899' } : {}}>
                            {p.nom.length > 20 ? p.nom.substring(0, 20) + '…' : p.nom}
                          </span>
                        );
                      })}
                      {nbProduits > 3 && (
                        <span className="cours-tag cours-tag-more">+{nbProduits - 3}</span>
                      )}
                    </div>
                  </td>
                  <td><strong>{formatMontant(total)}</strong></td>
                  <td>{getStatusBadge(ins.statut)}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-icon" onClick={(e) => { e.stopPropagation(); openDetail(ins); }}>
                        <Eye size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <div className="empty-state">
          <ClipboardList size={48} />
          <h3>Aucune inscription trouvée</h3>
          <p>Modifiez vos filtres ou attendez de nouvelles inscriptions</p>
        </div>
      )}

      {selectedInscription && (() => {
        const ins = selectedInscription;
        const total = editMode ? editTotal : calculerTotalInscription(ins);
        const facture = getFactureByInscription(ins.id);
        const isEditable = ins.statut !== 'facturee';

        return (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  {editMode ? 'Modifier l\'inscription' : 'Détail de l\'inscription'}
                </h2>
                <button className="modal-close" onClick={closeModal}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                {/* Student info */}
                <div className="eleve-detail-header">
                  <div className="eleve-avatar-large" style={{ background: ins.color || '#8B5CF6' }}>
                    {ins.initials}
                  </div>
                  <div>
                    <h2 style={{ margin: 0 }}>{ins.eleve.prenom} {ins.eleve.nom}</h2>
                    <p className="eleve-meta">
                      {calculerAge(ins.eleve.dateNaissance)} ans · Inscrit le {formatDate(ins.dateInscription)}
                    </p>
                    <div style={{ marginTop: '0.5rem' }}>{getStatusBadge(ins.statut)}</div>
                  </div>
                </div>

                <div className="detail-sections">
                  <div className="detail-section">
                    <h4><User size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Coordonnées élève</h4>
                    <div className="detail-grid">
                      <span style={{ color: 'var(--gray-500)' }}><Mail size={12} /> {ins.eleve.email}</span>
                      <span style={{ color: 'var(--gray-500)' }}><Phone size={12} /> {ins.eleve.telephone}</span>
                      <span style={{ color: 'var(--gray-500)' }}><Calendar size={12} /> {formatDate(ins.eleve.dateNaissance)}</span>
                      <span style={{ color: 'var(--gray-500)' }}><MapPin size={12} /> {ins.eleve.adresse}</span>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4><User size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Responsable : {ins.responsable.prenom} {ins.responsable.nom} ({ins.responsable.lien})</h4>
                    <div className="detail-grid">
                      <span style={{ color: 'var(--gray-500)' }}><Mail size={12} /> {ins.responsable.email}</span>
                      <span style={{ color: 'var(--gray-500)' }}><Phone size={12} /> {ins.responsable.telephone}</span>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span><Euro size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Produits sélectionnés</span>
                      <strong style={{ color: 'var(--primary)' }}>{formatMontant(total)}</strong>
                    </h4>

                    {!editMode ? (
                      <div className="detail-cours-list">
                        {(ins.produitsIds).map(pid => {
                          const p = getProduit(pid);
                          if (!p) return null;
                          return (
                            <div key={pid} className="detail-cours-item">
                              <div>
                                <span className="badge" style={
                                  p.categorie === 'cours'
                                    ? { background: 'var(--primary-100)', color: 'var(--primary)' }
                                    : p.categorie === 'instrument'
                                      ? { background: '#FDF2F8', color: '#EC4899' }
                                      : { background: 'var(--gray-200)', color: 'var(--gray-600)' }
                                }>
                                  {p.categorie}
                                </span>
                                <span style={{ marginLeft: '0.5rem' }}>{p.nom}</span>
                              </div>
                              <strong>{formatMontant(p.tarif)}</strong>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <>
                        <h5 style={{ margin: '0.75rem 0 0.5rem', fontSize: '0.8125rem', color: 'var(--gray-600)' }}>Cours de musique</h5>
                        <div className="cours-grid" style={{ marginBottom: '1rem' }}>
                          {getCoursProduits().map(c => {
                            const isSelected = editProduitsIds.includes(c.id);
                            return (
                              <div
                                key={c.id}
                                className={`cours-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => toggleProduit(c.id)}
                              >
                                <div className="cours-card-content">
                                  <span className="cours-nom">{c.nom}</span>
                                  <span className="cours-tarif">{formatMontant(c.tarif)}</span>
                                </div>
                                <div className="cours-check">
                                  {isSelected && <Check size={20} />}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {availableInstruments.length > 0 && (
                          <>
                            <h5 style={{ margin: '0.75rem 0 0.5rem', fontSize: '0.8125rem', color: 'var(--gray-600)' }}>Instruments</h5>
                            <div className="cours-grid">
                              {availableInstruments.map(inst => {
                                const isSelected = editProduitsIds.includes(inst.id);
                                return (
                                  <div
                                    key={inst.id}
                                    className={`cours-card ${isSelected ? 'selected' : ''}`}
                                    onClick={() => toggleProduit(inst.id)}
                                  >
                                    <div className="cours-card-content">
                                      <span className="cours-nom">{inst.nom}</span>
                                      <span className="cours-tarif">{formatMontant(inst.tarif)}</span>
                                    </div>
                                    <div className="cours-check">
                                      {isSelected && <Check size={20} />}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>

                  {ins.rgpdConsent && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: 'var(--success)' }}>
                      <Shield size={14} /> Consentement RGPD accordé
                    </div>
                  )}

                  {facture && (
                    <div className="detail-section" style={{ background: '#D1FAE5' }}>
                      <h4><FileText size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Facture générée</h4>
                      <p style={{ margin: 0 }}>
                        {facture.numero} — {formatDate(facture.date)} — {formatMontant(facture.totalNet)}
                        <span style={{ marginLeft: '0.5rem' }}>
                          {facture.statut === 'payee' && <span className="badge badge-success">Payée</span>}
                          {facture.statut === 'partielle' && <span className="badge badge-warning">Partielle</span>}
                          {facture.statut === 'impayee' && <span className="badge badge-danger">Impayée</span>}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                {editMode ? (
                  <>
                    <button className="btn btn-secondary" onClick={() => setEditMode(false)}>Annuler</button>
                    <button className="btn btn-primary" onClick={() => setEditMode(false)}>
                      <Check size={16} /> Sauvegarder
                    </button>
                  </>
                ) : (
                  <>
                    {isEditable && (
                      <button className="btn btn-secondary" onClick={startEdit}>
                        <Edit2 size={16} /> Modifier les produits
                      </button>
                    )}
                    {ins.statut === 'en_attente' && (
                      <button className="btn btn-success">
                        <CheckCircle size={16} /> Valider l'inscription
                      </button>
                    )}
                    {ins.statut === 'validee' && (
                      <button className="btn btn-primary">
                        <FileText size={16} /> Générer la facture
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Inscriptions;
